import * as React from 'react';
import { doRectsIntersect } from '../../helpers';
const useVisibilityContainer = (opts = {}) => {
    const buffer = opts.buffer || 100;
    const containerRef = React.useRef(null);
    const visibilityRefs = React.useRef([]);
    const listenersMap = React.useRef(new WeakMap());
    const checkVisibilityRaf = React.useRef(null);
    const updateListenersRaf = React.useRef(null);
    const addRefToVisibilbityMap = (ref, listener) => {
        visibilityRefs.current.push(ref);
        listenersMap.current.set(ref, listener);
        if (checkVisibilityRaf.current) {
            cancelAnimationFrame(checkVisibilityRaf.current);
        }
        checkVisibilityRaf.current = requestAnimationFrame(checkVisibility);
    };
    const removeRefFromVisibilbityMap = (ref) => {
        const refIndex = visibilityRefs.current.indexOf(ref);
        if (refIndex === -1)
            return;
        visibilityRefs.current.splice(refIndex, 1);
        listenersMap.current.delete(ref);
        if (checkVisibilityRaf.current) {
            cancelAnimationFrame(checkVisibilityRaf.current);
        }
        checkVisibilityRaf.current = requestAnimationFrame(checkVisibility);
    };
    const checkVisibility = React.useCallback(() => {
        if (!containerRef.current || visibilityRefs.current.length === 0)
            return;
        const containerRect = containerRef.current.getBoundingClientRect();
        const visibleRefs = [];
        visibilityRefs.current.forEach((ref) => {
            if (!ref.current)
                return;
            const visibilityRect = ref.current.getBoundingClientRect();
            const isVisible = doRectsIntersect(containerRect, {
                top: visibilityRect.top - buffer,
                left: visibilityRect.left - buffer,
                width: visibilityRect.width + buffer * 2,
                height: visibilityRect.height + buffer * 2,
            });
            if (isVisible) {
                visibleRefs.push(ref);
            }
        });
        // Defer firing listeners until next frame.
        if (updateListenersRaf.current) {
            cancelAnimationFrame(updateListenersRaf.current);
        }
        updateListenersRaf.current = requestAnimationFrame(() => {
            visibleRefs.forEach((ref) => {
                const listener = listenersMap.current.get(ref);
                if (listener)
                    listener(true);
            });
            updateListenersRaf.current = null;
        });
    }, [buffer]);
    React.useEffect(() => {
        var _a;
        // Check visibility on scroll.
        (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('scroll', checkVisibility, false);
        // Check visibility on resize.
        window.addEventListener('resize', checkVisibility, false);
        // Check visibility on orientation change.
        window.addEventListener('orientation', checkVisibility, false);
        return () => {
            if (containerRef.current) {
                containerRef.current.removeEventListener('scroll', checkVisibility, false);
                window.removeEventListener('resize', checkVisibility, false);
                window.removeEventListener('orientation', checkVisibility, false);
            }
        };
    }, [checkVisibility]);
    // Calculate visiblity on state change.
    const didMountRef = React.useRef(false);
    React.useEffect(() => {
        if (didMountRef.current) {
            checkVisibility();
        }
        else {
            didMountRef.current = true;
        }
    });
    return { containerRef, addRefToVisibilbityMap, removeRefFromVisibilbityMap };
};
export default useVisibilityContainer;
//# sourceMappingURL=useVisibilityContainer.js.map